import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Select, MenuItem, Typography, CircularProgress, Button,
  TextField, Grid, Pagination, Box, InputAdornment, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper function to clean the data - remove system fields
const cleanData = (data) => {
  if (!data) return null;
  
  const fieldsToExclude = [
    'id', 'company_id', 'createdAt', 'updatedAt',
    '_iv', '_tag', '_id', 'created_at', 'updated_at'
  ];
  
  const cleaned = {};
  Object.keys(data).forEach(key => {
    // Check if the field should be excluded
    const shouldExclude = fieldsToExclude.some(exclude => 
      key === exclude || key.endsWith('_iv') || key.endsWith('_tag')
    );
    
    if (!shouldExclude) {
      cleaned[key] = data[key];
    }
  });
  
  return cleaned;
};

// Format value for display
const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const AuditLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Read state directly from URL search params
  const page = parseInt(searchParams.get("page")) || 1;
  const actionFilter = searchParams.get("action") || "";
  const tableFilter = searchParams.get("table") || "";
  const emailSearch = searchParams.get("email") || "";

  // Local input state to prevent API calls on every keypress
  const [emailInput, setEmailInput] = useState(emailSearch);
  const [companyConfig, setCompanyConfig] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.company_id) {
          const res = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data && res.data.data) {
            setCompanyConfig(res.data.data);
          }
        }
      } catch (err) {
        // console.error("Error fetching company config:", err);
      }
    };
    fetchConfig();
  }, []);

  const formatAuditDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (companyConfig?.timezone) {
      return date.toLocaleString('en-US', { timeZone: companyConfig.timezone });
    }
    return date.toLocaleString();
  };

  // Sync email input field if the URL query parameter changes
  useEffect(() => {
    setEmailInput(emailSearch);
  }, [emailSearch]);

  // Helper to update URL search parameters
  const updateUrlParams = (newParams) => {
    setSearchParams(prev => {
      const updated = new URLSearchParams(prev);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          updated.set(key, value);
        } else {
          updated.delete(key);
        }
      });
      // Reset page to 1 when filters change (unless page is explicitly updated)
      if (newParams.page === undefined) {
        updated.set("page", "1");
      }
      return updated;
    });
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/admin/audit-logs`, {
        params: {
          page,
          limit: 30,
          action: actionFilter || undefined,
          tableName: tableFilter || undefined,
          userEmail: emailSearch || undefined
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data.status === "success") {
        setLogs(response.data.data.logs);
        setTotalPages(response.data.data.pages);
        setTotalCount(response.data.data.total);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Fetch logs whenever query parameters in the URL change
  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, tableFilter, emailSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateUrlParams({ email: emailInput, page: "1" });
  };

  const handleClearFilters = () => {
    setEmailInput("");
    setSearchParams({ page: "1" });
  };

  const handleOpenDetail = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setSelectedLog(null);
    setDetailModalOpen(false);
  };

  const getActionChipColor = (action) => {
    switch (action) {
      case "CREATE":
        return "success";
      case "UPDATE":
        return "warning";
      case "DELETE":
        return "error";
      default:
        return "default";
    }
  };

  const formatVal = (val) => {
    if (val === null || val === undefined) return "NULL";
    if (typeof val === "object") return JSON.stringify(val);
    return String(val);
  };

  // Get display data for the detail modal
  const getDisplayData = (log) => {
    if (!log) return null;
    
    let dataToShow = null;
    if (log.action === "CREATE" && log.newData) {
      dataToShow = cleanData(log.newData);
    } else if (log.action === "DELETE" && log.oldData) {
      dataToShow = cleanData(log.oldData);
    } else if (log.action === "UPDATE" && log.newData) {
      dataToShow = cleanData(log.newData);
    }
    return dataToShow;
  };

  // Get old data for update action
  const getOldDisplayData = (log) => {
    if (!log || log.action !== "UPDATE") return null;
    if (log.oldData) {
      return cleanData(log.oldData);
    }
    return null;
  };

  return (
  <Box sx={{ p: 4, width: "100%" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 3 }}>
        Audit Logs
      </Typography>

      {/* Filters Area */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <form onSubmit={handleSearchSubmit}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={actionFilter}
                  label="Action"
                  onChange={(e) => updateUrlParams({ action: e.target.value })}
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="CREATE">CREATE</MenuItem>
                  <MenuItem value="UPDATE">UPDATE</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Filter by Table Name"
                placeholder="e.g. users, comments"
                value={tableFilter}
                onChange={(e) => updateUrlParams({ tableName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Search by User Email"
                placeholder="Search email..."
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button type="submit" size="small" variant="contained" sx={{ minWidth: "40px", p: "4px" }}>
                        <SearchIcon fontSize="small" />
                      </Button>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Audit Logs Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography variant="body1" color="textSecondary">
              No audit logs found.
            </Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Target Table</TableCell>
                  {/* <TableCell sx={{ fontWeight: "bold" }}>Record ID</TableCell> */}
                  <TableCell sx={{ fontWeight: "bold" }}>User (Email)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: "bold", align: "center" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{formatAuditDate(log.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.action}
                        color={getActionChipColor(log.action)}
                        size="small"
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", color: "#e91e63" }}>
                      {log.tableName}
                    </TableCell>
                    {/* <TableCell sx={{ fontFamily: "monospace" }}>{log.recordId || "N/A"}</TableCell> */}
                    <TableCell>{log.userEmail || "System/Anonymous"}</TableCell>
                    <TableCell>
                      <Chip label={log.userRole || "system"} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDetail(log)}
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 3, backgroundColor: "#fbfbfb" }}>
              <Typography variant="body2" color="textSecondary">
                Showing {logs.length} of {totalCount} logs
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, p) => updateUrlParams({ page: String(p) })}
                color="primary"
              />
            </Box>
          </>
        )}
      </TableContainer>

      {/* Details View Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f5f5f5" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1a237e" }}>
            Log Change Details
          </Typography>
          <IconButton onClick={handleCloseDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          {selectedLog && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary"><strong>Action Triggered:</strong></Typography>
                  <Chip label={selectedLog.action} color={getActionChipColor(selectedLog.action)} size="small" sx={{ mt: 0.5, fontWeight: "bold" }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary"><strong>Target Entity:</strong></Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{selectedLog.tableName} (ID: {selectedLog.recordId || "N/A"})</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary"><strong>Performed By:</strong></Typography>
                  <Typography variant="body1">{selectedLog.userEmail || "System/Unauthenticated"} ({selectedLog.userRole || "System"})</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary"><strong>Timestamp:</strong></Typography>
                  <Typography variant="body1">{formatAuditDate(selectedLog.createdAt)}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", borderBottom: "1px solid #ddd", pb: 1, mb: 2 }}>
                Data Changes
              </Typography>

              {/* Display for UPDATE action - Show Old vs New */}
              {selectedLog.action === "UPDATE" ? (
                <>
                  {selectedLog.oldData && selectedLog.newData ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#d32f2f", mb: 1 }}>
                        Old Values
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, mb: 3 }}>
                        <Table size="small">
                          <TableBody>
                            {Object.entries(getOldDisplayData(selectedLog) || {}).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell sx={{ fontWeight: "bold", width: "40%", backgroundColor: "#fafafa" }}>
                                  {key}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: "#ffebee", color: "#c62828" }}>
                                  {formatDisplayValue(value)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#388e3c", mb: 1 }}>
                        New Values
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                        <Table size="small">
                          <TableBody>
                            {Object.entries(getDisplayData(selectedLog) || {}).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell sx={{ fontWeight: "bold", width: "40%", backgroundColor: "#fafafa" }}>
                                  {key}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                                  {formatDisplayValue(value)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No data changes logged for this update.</Typography>
                  )}
                </>
              ) : (
                /* Display for CREATE and DELETE actions */
                <>
                  {(() => {
                    const displayData = getDisplayData(selectedLog);
                    if (displayData && Object.keys(displayData).length > 0) {
                      return (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                          <Table size="small">
                            <TableHead sx={{ backgroundColor: "#fafafa" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", width: "40%" }}>Field Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(displayData).map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#fafafa" }}>
                                    {key}
                                  </TableCell>
                                  <TableCell>
                                    {value === null || value === undefined ? (
                                      <Typography variant="body2" color="textSecondary">N/A</Typography>
                                    ) : typeof value === 'object' ? (
                                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                        {JSON.stringify(value, null, 2)}
                                      </pre>
                                    ) : (
                                      String(value)
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    } else {
                      return (
                        <Typography variant="body2" color="textSecondary">
                          No data available for this action.
                        </Typography>
                      );
                    }
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: "#f5f5f5" }}>
          <Button onClick={handleCloseDetail} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;